import { useRef, useCallback, useEffect } from "react";

/**
 * Custom hook for managing signature pad functionality
 * Handles both mouse and touch events efficiently
 * Optimized to avoid MouseEvent conversion overhead
 */
export const useSignaturePad = (
  canvasRef,
  onSignatureChange,
  signaturePadRef = null,
  personnelSignaturePadRef = null,
  setHasSignature = null,
  setHasPersonnelSignature = null
) => {
  const isDrawingRef = useRef(false);
  const lastXRef = useRef(0);
  const lastYRef = useRef(0);

  // Helper to get canvas element from ref (handles both ref objects and direct elements)
  const getCanvasElement = useCallback((ref) => {
    if (!ref) return null;
    // Check if it's a ref object (has .current property)
    if (typeof ref === "object" && "current" in ref) {
      return ref.current;
    }
    // Otherwise, assume it's already a canvas element
    return ref;
  }, []);

  // Initialize canvas context
  useEffect(() => {
    const canvas = getCanvasElement(canvasRef);
    if (!canvas || typeof canvas.getContext !== "function") return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [canvasRef, getCanvasElement]);

  // Get coordinates from event (supports both mouse and touch) - optimized
  const getEventCoordinates = useCallback(
    (e, canvas) => {
      const canvasEl = getCanvasElement(canvas);
      if (!canvasEl) return { x: 0, y: 0 };
      const rect = canvasEl.getBoundingClientRect();
      if (e.touches && e.touches.length > 0) {
        // Touch event - use coordinates directly
        return {
          x: e.touches[0].clientX - rect.left,
          y: e.touches[0].clientY - rect.top,
        };
      } else {
        // Mouse event
        return {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
      }
    },
    [getCanvasElement]
  );

  // Determine which signature pad is being used and update state accordingly
  const updateSignatureState = useCallback(
    (canvas) => {
      if (!setHasSignature && !setHasPersonnelSignature) return;

      const canvasEl = getCanvasElement(canvas);
      if (!canvasEl) return;

      // Check if this is the main signature pad
      if (
        signaturePadRef &&
        (canvasEl === signaturePadRef || canvasEl === signaturePadRef.current)
      ) {
        if (setHasSignature) setHasSignature(true);
      }
      // Check if this is the personnel signature pad
      else if (
        personnelSignaturePadRef &&
        (canvasEl === personnelSignaturePadRef ||
          canvasEl === personnelSignaturePadRef.current)
      ) {
        if (setHasPersonnelSignature) setHasPersonnelSignature(true);
      }
      // Default to main signature pad
      else if (setHasSignature) {
        setHasSignature(true);
      }
    },
    [
      signaturePadRef,
      personnelSignaturePadRef,
      setHasSignature,
      setHasPersonnelSignature,
      getCanvasElement,
    ]
  );

  // Start drawing
  const handleStart = useCallback(
    (e, canvas) => {
      e.preventDefault();
      const canvasEl = getCanvasElement(canvas);
      if (!canvasEl || typeof canvasEl.getContext !== "function") return;

      const coords = getEventCoordinates(e, canvasEl);
      isDrawingRef.current = true;
      lastXRef.current = coords.x;
      lastYRef.current = coords.y;

      const ctx = canvasEl.getContext("2d");
      if (!ctx) return;
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
    },
    [getEventCoordinates, getCanvasElement]
  );

  // Continue drawing
  const handleMove = useCallback(
    (e, canvas) => {
      if (!isDrawingRef.current) return;
      e.preventDefault();

      // For mouse events, check if button is pressed
      if (e.buttons !== undefined && e.buttons !== 1) return;

      const canvasEl = getCanvasElement(canvas);
      if (!canvasEl || typeof canvasEl.getContext !== "function") return;

      const ctx = canvasEl.getContext("2d");
      const coords = getEventCoordinates(e, canvasEl);

      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();

      lastXRef.current = coords.x;
      lastYRef.current = coords.y;

      // Update signature state
      updateSignatureState(canvasEl);
    },
    [getEventCoordinates, updateSignatureState, getCanvasElement]
  );

  // Stop drawing
  const handleEnd = useCallback(
    (e, canvas) => {
      if (!isDrawingRef.current) return;
      e.preventDefault();

      const canvasEl = getCanvasElement(canvas);
      if (!canvasEl || typeof canvasEl.getContext !== "function") return;

      const ctx = canvasEl.getContext("2d");
      ctx.beginPath();

      isDrawingRef.current = false;

      // Update signature state
      updateSignatureState(canvasEl);
    },
    [updateSignatureState, getCanvasElement]
  );

  // Clear signature
  const clearSignature = useCallback(
    (canvas) => {
      const canvasEl = getCanvasElement(canvas);
      if (!canvasEl || typeof canvasEl.getContext !== "function") return;

      const ctx = canvasEl.getContext("2d");
      ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

      // Update signature state
      if (
        signaturePadRef &&
        (canvasEl === signaturePadRef || canvasEl === signaturePadRef.current)
      ) {
        if (setHasSignature) setHasSignature(false);
      } else if (
        personnelSignaturePadRef &&
        (canvasEl === personnelSignaturePadRef ||
          canvasEl === personnelSignaturePadRef.current)
      ) {
        if (setHasPersonnelSignature) setHasPersonnelSignature(false);
      } else if (setHasSignature) {
        setHasSignature(false);
      }
    },
    [
      signaturePadRef,
      personnelSignaturePadRef,
      setHasSignature,
      setHasPersonnelSignature,
    ]
  );

  // Get signature as data URL
  const getSignatureData = useCallback(
    (canvas) => {
      const canvasEl = getCanvasElement(canvas);
      if (!canvasEl || typeof canvasEl.toDataURL !== "function") return null;
      return canvasEl.toDataURL("image/png");
    },
    [getCanvasElement]
  );

  return {
    handleStart,
    handleMove,
    handleEnd,
    clearSignature,
    getSignatureData,
  };
};
